import { useEffect, createContext, useRef } from "react";
import * as signalR from "@microsoft/signalr";
import axios from "axios";
import logger from "../utils/logger";
import { Eventbus } from "../utils/index";
import { events } from "../utils/constants/index";
import ToastService from "./toast.service";
import { AuthenticationService } from "./authentication.service";
import config from "../config";

// Create a dedicated axios instance for SignalR that bypasses the main interceptors
const signalRAxios = axios.create({
  baseURL: config.paymintApiUrl,
  headers: { 'content-type': 'application/json' },
  withCredentials: true
});

// Add only auth interceptor (not the response data extraction)
signalRAxios.interceptors.request.use(async (request) => {
  let isAuthenticated = AuthenticationService.isAuthenticated();
  if (!isAuthenticated) {
    await AuthenticationService.refreshToken();
    isAuthenticated = AuthenticationService.isAuthenticated();
  }
  if (!isAuthenticated) {
    return Promise.reject(new Error('User not authenticated'));
  }
  request.withCredentials = true;
  return request;
}, (error) => Promise.reject(error));

async function getSignalRConnectionDirect(abortSignal) {
  try {
    const response = await signalRAxios.get('/api/SignalR/negotiate', {
      signal: abortSignal
    });
    // Return the actual data, not the wrapped version
    return response.data?.data || response.data;
  } catch (error) {
    logger.error('Error fetching SignalR connection:', error);
    throw error;
  }
}

// Generic message processor for data change events
const processDataChangeMessage = (eventName, message) => {
  logger.debug(`Processing SignalR message for ${eventName}:`, message);
  try {
    // Special handling for delete events
    if (eventName.includes(".delete")) {
      logger.log(`Processing ${eventName} deletion for ID:`, message);
      // Parse the message if it's a JSON string, otherwise use as-is
      let id = message;
      if (typeof message === "string") {
        try {
          id = JSON.parse(message);
        } catch (e) {
          // If it's not valid JSON, use the string as-is
          logger.debug(`Delete message is not JSON, using as string:`, message);
        }
      }
      Eventbus.emit(eventName, id);
      return;
    }
    // For other events, parse JSON strings or emit objects directly
    if (typeof message === "string") {
      try {
        const parsed = JSON.parse(message);
        Eventbus.emit(eventName, parsed);
      } catch (e) {
        logger.warn(`Failed to parse SignalR message for ${eventName}:`, message);
      }
    } else {
      Eventbus.emit(eventName, message);
    }
  } catch (error) {
    logger.error(`Error processing SignalR ${eventName}:`, error, message);
  }
}

export async function connectToSignalR({ connection }) {
  logger.log("Connecting to SignalR...");
  try {
    if (!connection) {
      throw new Error("No connection object provided");
    }

    const hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(connection.url, {
        accessTokenFactory: () => {
          // Use the token from the negotiate response
          // This gets called by SignalR internally, but we just return the same token
          logger.debug("SignalR using connection token");
          return connection.accessToken;
        },
      })
      .configureLogging(signalR.LogLevel.Information)
      // Don't use automatic reconnect - we handle reconnection manually to get fresh tokens
      .build();

    // Re-register all event handlers (called after reconnection)
    const registerAllHandlers = () => {
      logger.log("Re-registering all SignalR event handlers");
      Object.entries(events.SIGNALR).forEach(([eventKey, eventName]) => {
        logger.debug(`Registering SignalR handler for: ${eventName}`);
        try {
          if (typeof hubConnection.off === 'function') {
            hubConnection.off(eventName);
          }
        } catch (e) {
          logger.debug(`Error calling off() for ${eventName}:`, e);
        }
        hubConnection.on(eventName, (message) => processDataChangeMessage(eventName, message));
      });
    };

    // Dynamically register handlers for all SignalR events
    Object.entries(events.SIGNALR).forEach(([eventKey, eventName]) => {
      logger.log(`Registering SignalR handler for: ${eventName}`);

      // De-duplicate handlers: remove any existing handler for this event first
      try {
        // hubConnection.off is safe even if no handler exists
        if (typeof hubConnection.off === 'function') {
          hubConnection.off(eventName);
        }
      } catch (e) {
        logger.debug(`Error calling off() for ${eventName}:`, e);
      }

      // Generic handling for data change events
      hubConnection.on(eventName, (message) => processDataChangeMessage(eventName, message));
    });

    await hubConnection.start();
    logger.log("SignalR connection established");

    // Emit connected event
    Eventbus.emit(events.SIGNALR.CONNECTED);

    // Handle connection events
    hubConnection.onclose(async (error) => {
      logger.log("SignalR connection closed", error);
      Eventbus.emit(events.SIGNALR.DISCONNECTED);
      
      // Clear the connection so it will be recreated with fresh tokens
      connectionManager.connection = null;
      
      // Attempt to reconnect after a short delay
      setTimeout(() => {
        logger.log("Attempting to reconnect SignalR after connection close");
        connectionManager.getConnection().catch((err) => {
          logger.error("Failed to reconnect SignalR:", err);
        });
      }, 2000);
    });
    hubConnection.onreconnected(() => {
      logger.log("SignalR reconnected");
      // Re-register handlers after reconnection
      registerAllHandlers();
      Eventbus.emit(events.SIGNALR.CONNECTED);
    });
    hubConnection.onreconnecting(() => {
      logger.log("SignalR reconnecting");
      Eventbus.emit(events.SIGNALR.DISCONNECTED);
    });

    // Return the connection object in case the consumer needs to stop it or add more handlers
    return hubConnection;
  } catch (error) {
    logger.error("Error connecting to SignalR:", error);
    throw error;
  }
}

// Global connection manager to persist across component re-mounts
const connectionManager = {
  connection: null,
  isConnecting: false,
  lastAttempt: 0,
  attemptCount: 0,
  lastHealthCheck: 0,
  healthCheckInterval: 30000, // Check connection every 30 seconds
  healthCheckTimerId: null,
  lastReconnectAttempt: 0,
  minReconnectInterval: 5000, // Don't try to reconnect more than once per 5 seconds
  // minimum ms between attempts
  minRetryInterval: 1000,
  // max retry attempts before giving up for this invocation
  maxAttempts: 5,

  async verifyConnectionHealth() {
    // Don't spam health checks
    const now = Date.now();
    if (now - this.lastHealthCheck < this.healthCheckInterval) {
      return this.connection && this.connection.state === signalR.HubConnectionState.Connected;
    }

    this.lastHealthCheck = now;

    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      logger.warn("Connection health check failed: not connected");
      
      // Throttle reconnection attempts to prevent rapid reconnect loops
      if (now - this.lastReconnectAttempt < this.minReconnectInterval) {
        logger.debug("Reconnect attempt throttled, waiting before retry");
        return false;
      }
      
      this.lastReconnectAttempt = now;
      logger.warn("Attempting reconnection due to failed health check");
      // Trigger reconnection if connection is dead
      this.connection = null;
      await this.getConnection().catch((error) => {
        logger.error("Reconnection attempt failed during health check:", error);
      });
      return false;
    }

    // Verify connection is responsive by checking if it has active handlers
    try {
      logger.debug("SignalR connection health check passed");
      return true;
    } catch (error) {
      logger.error("SignalR connection health check failed:", error);
      return false;
    }
  },

  startPeriodicHealthCheck() {
    if (this.healthCheckTimerId) {
      logger.debug("Health check already running");
      return;
    }

    logger.log("Starting periodic SignalR health checks every", this.healthCheckInterval, "ms");
    this.healthCheckTimerId = setInterval(() => {
      this.verifyConnectionHealth().catch((error) => {
        logger.error("Error during periodic health check:", error);
      });
    }, this.healthCheckInterval);
  },

  stopPeriodicHealthCheck() {
    if (this.healthCheckTimerId) {
      logger.log("Stopping periodic SignalR health checks");
      clearInterval(this.healthCheckTimerId);
      this.healthCheckTimerId = null;
    }
  },

  async getConnection() {
    if (this.connection && this.connection.state === signalR.HubConnectionState.Connected) {
      // Verify the connection is still healthy (hasn't timed out server-side)
      const isHealthy = await this.verifyConnectionHealth();
      if (isHealthy) {
        logger.log("Returning existing SignalR connection");
        return this.connection;
      } else {
        logger.warn("Existing connection is stale, reconnecting...");
        this.connection = null;
      }
    }

    if (this.isConnecting) {
      logger.log("Connection already in progress, waiting...");
      // Wait for the current connection attempt to complete with a timeout
      let waitCount = 0;
      const maxWait = 100; // 10 seconds max wait (100 * 100ms)
      while (this.isConnecting && waitCount < maxWait) {
        await new Promise((resolve) => setTimeout(resolve, 100));
        waitCount++;
      }
      
      // If still connecting after timeout, force a new attempt
      if (this.isConnecting) {
        logger.error("Connection attempt timed out after 10 seconds, forcing new attempt");
        this.isConnecting = false;
      }
      
      if (this.connection && this.connection.state === signalR.HubConnectionState.Connected) {
        return this.connection;
      }
    }

    // Throttle rapid repeated calls
    const now = Date.now();
    if (now - this.lastAttempt < this.minRetryInterval) {
      logger.log("Throttling SignalR connection attempts");
      await new Promise((resolve) => setTimeout(resolve, this.minRetryInterval - (now - this.lastAttempt)));
    }

    return await this.createConnection();
  },

  async createConnection() {
    // Prevent overlapping attempts
    if (this.isConnecting) {
      logger.log("CreateConnection called while already connecting, returning existing attempt");
      let waitCount = 0;
      const maxWait = 100;
      while (this.isConnecting && waitCount < maxWait) {
        await new Promise((resolve) => setTimeout(resolve, 100));
        waitCount++;
      }
      if (this.isConnecting) {
        logger.error("CreateConnection wait timed out");
        this.isConnecting = false;
      }
      return this.connection;
    }

    this.isConnecting = true;
    this.lastAttempt = Date.now();
    this.attemptCount = 0;

    try {
      if (this.connection) {
        logger.log("Cleaning up existing SignalR connection...");
        await this.connection.stop().catch((err) => logger.error("Error stopping existing connection:", err));
        this.connection = null;
      }

      // Retry loop with exponential backoff up to maxAttempts
      let lastError;
      while (this.attemptCount < this.maxAttempts) {
        this.attemptCount += 1;
        const abortController = new AbortController();
        
        try {
          logger.log("Fetching SignalR connection details... attempt", this.attemptCount);
          
          // Add a timeout that will abort the request
          const timeoutId = setTimeout(() => {
            abortController.abort();
          }, 15000);
          
          const response = await getSignalRConnectionDirect(abortController.signal);
          
          clearTimeout(timeoutId);
          logger.debug("SignalR connection response:", response);
          
          if (!response?.url || !response?.accessToken) {
            logger.error("Invalid SignalR response structure:", {
              hasUrl: !!response?.url,
              hasAccessToken: !!response?.accessToken,
              responseKeys: response ? Object.keys(response) : null,
              fullResponse: response
            });
            throw new Error("Invalid SignalR connection response");
          }

          this.connection = await connectToSignalR({ connection: response });
          logger.log("SignalR connection successfully established");
          return this.connection;
        } catch (error) {
          lastError = error;
          logger.error(`SignalR connection attempt ${this.attemptCount} failed:`, error);
          
          // Don't retry if we've hit max attempts
          if (this.attemptCount >= this.maxAttempts) {
            logger.error("Max connection attempts reached");
            break;
          }
          
          // simple exponential backoff
          const waitMs = Math.min(2000 * Math.pow(2, this.attemptCount - 1), 30000);
          logger.log(`Waiting ${waitMs}ms before retrying SignalR connection`);
          // small jitter
          const jitter = Math.floor(Math.random() * 200);
          await new Promise((resolve) => setTimeout(resolve, waitMs + jitter));
        }
      }

      // All attempts failed
      logger.error("All SignalR connection attempts failed", lastError);
      // Throttle toasts so we don't spam UI on rapid failures
      ToastService.error("Error establishing SignalR connection");
      throw lastError;
    } finally {
      this.isConnecting = false;
    }
  },

  async disconnect() {
    if (this.connection) {
      logger.log("Disconnecting SignalR...");
      await this.connection.stop().catch((err) => logger.error("Error stopping SignalR connection:", err));
      this.connection = null;
    }
    this.stopPeriodicHealthCheck();
    this.isConnecting = false;
    this.attemptCount = 0;
  },
};

const signalRContext = createContext();

export const getSignalRConnectionState = () => {
  return connectionManager.connection && connectionManager.connection.state === signalR.HubConnectionState.Connected;
};

// Ensure connection is healthy (call this after API calls)
export const ensureSignalRConnected = async () => {
  try {
    const connection = await connectionManager.getConnection();
    if (connection && connection.state === signalR.HubConnectionState.Connected) {
      logger.debug("SignalR connection verified after API call");
      return true;
    }
  } catch (error) {
    logger.error("Failed to verify SignalR connection:", error);
  }
  return false;
};

// Export for testing purposes - allows tests to reset state between runs
export const __resetConnectionManagerForTesting = () => {
  connectionManager.connection = null;
  connectionManager.isConnecting = false;
  connectionManager.lastAttempt = 0;
  connectionManager.attemptCount = 0;
  connectionManager.lastHealthCheck = 0;
  connectionManager.lastReconnectAttempt = 0;
  connectionManager.stopPeriodicHealthCheck();
};

// Export internals for testing
export { processDataChangeMessage, connectionManager };

export default function SignalRService() {
  const initializationRef = useRef(false);

  useEffect(() => {
    // Only initialize once during the application lifecycle
    if (initializationRef.current) {
      logger.log("SignalR service already initialized, skipping...");
      return;
    }

    initializationRef.current = true;
    logger.log("Initializing SignalR service...");

    // Initialize the connection and only start health checks after successful connection
    connectionManager.getConnection()
      .then(() => {
        logger.log("Initial SignalR connection established, starting periodic health checks");
        connectionManager.startPeriodicHealthCheck();
      })
      .catch((error) => {
        logger.error("Failed to initialize SignalR connection:", error);
        // Still start health checks even if initial connection fails
        // so it can retry periodically
        connectionManager.startPeriodicHealthCheck();
      });

    return () => {
      connectionManager.disconnect().catch((error) => {
        logger.error("Error during SignalR service cleanup:", error);
      });
      logger.log("SignalR service cleanup");
    };
  }, []);

  return (
    <signalRContext.Provider
      value={{
        connectToSignalR: () => connectionManager.getConnection(),
        disconnect: () => connectionManager.disconnect(),
      }}
    ></signalRContext.Provider>
  );
}
